// SLA/time helpers for the Onboarding Support board — countdowns, dwell
// clocks, and the tone (left-stripe color) system. Locked palette: canvas
// #F5F7FA, surface #FFFFFF, border #E3E8EF; urgency shows as a colored left
// stripe in the default (classic) tile style; the per-user "solid" style
// (2026-08-06) promotes the tone to a whole-tile fill instead.

import moment from 'moment-timezone';

import { CT } from '../../../lib/dateRange';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;

export const STRIPE = {
  neutral: '#CBD5E1', // slate-300 — nothing due right now
  upcoming: '#93C5FD', // blue-300 — a step later today
  due: '#F59E0B', // amber-500 — inside the window, act now
  overdue: '#F87171', // red-400 (soft, per Nic — never dark rose)
  quiet: '#E3E8EF', // breather/quiet days
  contactFresh: '#34D399', // emerald-400 — just made contact
  done: '#34D399',
  automated: '#A78BFA', // violet-400 — the current step sends itself
  // (No pink "fresh" tone since 2026-10-23: a new lead waiting for Call #1
  // is the usual blue; Day 1's New section is what sets new leads apart.)
};

// Solid tile mode: the whole tile wears the tone as a fill with white text,
// so fills run one shade deeper than the stripes to hold white-text
// contrast. Keyed by STRIPE hex — tone objects keep carrying `stripe`, and
// solid callers translate with fillFor().
const SOLID_FILL = {
  [STRIPE.neutral]: '#64748B', // slate-500
  [STRIPE.upcoming]: '#3B82F6', // blue-500
  [STRIPE.due]: '#F59E0B', // amber-500 (already deep enough)
  [STRIPE.overdue]: '#EF4444', // red-500
  [STRIPE.quiet]: '#94A3B8', // slate-400
  [STRIPE.contactFresh]: '#10B981', // emerald-500 (covers done too)
  [STRIPE.automated]: '#8B5CF6', // violet-500
};

export const fillFor = (stripe) =>
  SOLID_FILL[stripe] || SOLID_FILL[STRIPE.neutral];

// ACCURACY RULES (these labels are what the team is judged against):
//  - under 2 minutes, durations show live SECONDS ("43s", "1m 12s") — the
//    tight first-response windows are minutes long, so minute granularity
//    was hiding the truth;
//  - at 2+ minutes, minutes are FLOORED, never rounded — "1m over" must
//    never appear at 31 seconds over.
export const shortDuration = (ms) => {
  const abs = Math.abs(ms);
  const s = Math.floor(abs / 1000);
  if (s < 60) return `${s}s`;
  const mins = Math.floor(s / 60);
  // Seconds show up to FIVE minutes (Nic 2026-08-15, was two). The tight
  // windows on this board are 3 minutes long, so flooring above 2 made a
  // brand-new intake lead read "2m left" one second after it landed and stay
  // there for the next 59 — a 3-minute window that never looks like one.
  // Minute granularity was hiding exactly the truth these labels exist for.
  if (mins < 5) return `${mins}m ${s % 60 ? `${s % 60}s` : ''}`.trim();
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60 ? `${mins % 60}m` : ''}`.trim();
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24 ? `${hours % 24}h` : ''}`.trim();
};

// "in 12m" / "in 43s" / "38s over" / "8m over"
export const countdownLabel = (target, now = new Date()) => {
  const diff = new Date(target).getTime() - now.getTime();
  if (diff > 0) return `in ${shortDuration(diff)}`;
  return `${shortDuration(diff)} over`;
};

// --- The working day (Nic 2026-08-15) --------------------------------------
// MIRROR of server/lib/workCalendar.js. The clock helpers below are the whole
// reason the board looked alive on a closed Saturday: every countdown was raw
// `target - now`, so a step whose window shut on Friday afternoon read
// "2d 14h over" and kept growing all weekend, as if the team were failing.
//
// These live here rather than in boardUtils because boardUtils imports FROM
// this module; the dependency only runs one way.

// EVERY calculation below runs in BOARD time (CT), never the browser's zone.
// The work calendar's hours and days are board-local, so reading them against
// `new Date().getHours()` silently shifts them for anyone outside Central: on
// a machine in Eastern, an 8-5 board "closed" at 4 PM CT, froze its
// countdowns an hour early, and rendered a window that opened at 16:22 CT as
// "opens at 5:22 PM". src/lib/dateRange.js carries the same warning for the
// dashboards. `CT` is the single client-side definition of board time.

const clockToMinutes = (t) => {
  const [h, m] = String(t || '0:0')
    .split(':')
    .map(Number);
  return (h || 0) * 60 + (m || 0);
};

export const isWorkingDay = (date, workCalendar = null) => {
  if (!workCalendar?.workingDays) return true;
  const m = moment.tz(date, CT);
  return (
    workCalendar.workingDays.includes(m.isoWeekday()) &&
    !(m.format('YYYY-MM-DD') in (workCalendar.daysOff || {}))
  );
};

/** Is the board open right now? Drives whether anything is allowed to tick. */
export const isWithinWorkingHours = (now = new Date(), workCalendar = null) => {
  if (!workCalendar?.startLocal || !workCalendar?.endLocal) return true;
  if (!isWorkingDay(now, workCalendar)) return false;
  const m = moment.tz(now, CT);
  const mins = m.hours() * 60 + m.minutes();
  return (
    mins >= clockToMinutes(workCalendar.startLocal) &&
    mins < clockToMinutes(workCalendar.endLocal)
  );
};

/**
 * Is `now` inside the final hour of a working day? Mirrors the server's
 * `inPushWindow` (push/unpush both 409 outside it) — if these two drift, the
 * UI offers a Push the server refuses. The one-hour width is fixed on the
 * server (PUSH_WINDOW_MINUTES), so it follows the closing time automatically.
 */
export const inPushWindow = (workCalendar = null, now = new Date()) => {
  if (!workCalendar?.endLocal) return false;
  if (!isWorkingDay(now, workCalendar)) return false;
  const m = moment.tz(now, CT);
  const mins = m.hours() * 60 + m.minutes();
  const close = clockToMinutes(workCalendar.endLocal);
  return mins >= close - 60 && mins < close;
};

/**
 * When a step stops being workable: the close of its own board day plus an
 * hour. Mirrors `workableUntil` on the server, which refuses the step past
 * this instant — if these two drift, the UI offers work that 409s.
 */
export const stepClosesAt = (step, workCalendar = null) => {
  const end = moment.tz(step.windowEndAt, CT);
  // After hours (2026-10-14): the window already runs to the end of the
  // evening; that is when it closes.
  if (step.meta?.afterHours) return end.toDate();
  if (!workCalendar?.endLocal) return end.add(1, 'day').toDate();
  const [h, m] = String(workCalendar.endLocal).split(':').map(Number);
  return end
    .hour(h || 0)
    .minute(m || 0)
    .second(0)
    .millisecond(0)
    .add(1, 'hour')
    .toDate();
};

export const stepIsClosed = (step, workCalendar = null, now = new Date()) =>
  now > stepClosesAt(step, workCalendar);

const clockText = (d) => moment.tz(d, CT).format('h:mm A');

/** "Monday at 8:00 AM" / "at 8:00 AM" when it is later the same board day. */
export const startsAtText = (instant, now = new Date()) => {
  const d = moment.tz(instant, CT);
  return d.isSame(moment.tz(now, CT), 'day')
    ? `at ${d.format('h:mm A')}`
    : d.format('dddd [at] h:mm A');
};

// Self-describing countdown for a step: the raw "in 1h 5m" was ambiguous —
// on an upcoming step it meant "the window OPENS then", on a due step the
// SAME format meant "the window CLOSES then". Words now carry the meaning:
//   upcoming: "opens in 1h 5m"   due: "1m 12s left"   overdue: "38s over"
export const stepCountdownLabel = (step, tone, now, workCalendar = null) => {
  const at = now || new Date();
  if (tone.state === 'automated') {
    // No urgency grammar: the sweep fires it when the window opens.
    const start = new Date(step.windowStartAt).getTime();
    return at.getTime() < start
      ? `sends ${countdownLabel(step.windowStartAt, now)}`
      : 'sending soon';
  }
  // NOTHING TICKS OUTSIDE THE WORKING DAY (Nic 2026-08-15). Once the board is
  // closed, a growing "2d 14h over" is not urgency, it is a lie about who is
  // failing. Past its closing time the step is done being workable and simply
  // reads "missed"; between closing time and the end of the grace hour it
  // says when it goes away; before opening it says when it comes back.
  if (stepIsClosed(step, workCalendar, at)) {
    return step.meta?.afterHours ? 'after hours' : 'missed';
  }
  if (workCalendar && !isWithinWorkingHours(at, workCalendar)) {
    // A window that is OPEN RIGHT NOW still counts down, even out of hours —
    // it is act-now work, and saying "opens at 5:22 PM" about a window that
    // opened at 5:22 PM is nonsense. Only the not-yet-open and the
    // already-passed cases go static.
    if (at < new Date(step.windowStartAt)) {
      return `opens ${startsAtText(step.windowStartAt, at)}`;
    }
    if (at >= new Date(step.windowEndAt)) {
      return `closes ${clockText(stepClosesAt(step, workCalendar))}`;
    }
  }
  if (tone.state === 'upcoming') {
    return `opens ${countdownLabel(step.windowStartAt, now)}`;
  }
  const c = countdownLabel(step.windowEndAt, now);
  if (c.startsWith('in ')) return `${c.slice(3)} left`;
  return c; // "Xs/Xm over"
};

// Window times show SECONDS whenever the window actually carries them (the
// arrival-anchored day-1 chain does; day-anchored steps are minute-clean).
// The card must never display "3:52" for a window that truly ends 3:52:47.
export const windowLabel = (step) => {
  const start = new Date(step.windowStartAt);
  const end = new Date(step.windowEndAt);
  const withSeconds = start.getSeconds() !== 0 || end.getSeconds() !== 0;
  const fmt = (d) =>
    d.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      ...(withSeconds ? { second: '2-digit' } : {}),
    });
  return `${fmt(start)} - ${fmt(end)}`;
};

// Intake tiles mirror Call #1's arrival-anchored window, which is +0 to +3
// minutes: a brand-new lead is ACT NOW from second zero, so there is no
// "upcoming" phase for the very first touch (Nic 2026-08-14). Amber "X left"
// while the window is open, red "X over" once it burned untaken. No color
// jump on Take — the tile lands in Day 1 wearing the color it already had,
// because the step card is running the identical window.
//
// This mirrors the SEED cadence (migration 156, d1_call_1). If Call #1's
// arrival offsets are ever edited in the cadence editor, these constants must
// move with them or the Intake tile will drift from the step card.
export const INTAKE_DEADLINE_MS = 3 * MINUTE;

// Awaiting-log grace (Nic 2026-08-24): a call placed in-window whose outcome
// isn't logged yet holds the tile frozen at the dial instant instead of
// burning red while the specialist wraps up. MIRROR of
// server/lib/leadCadence.js AWAITING_LOG_TTL_MS — keep identical.
export const AWAITING_LOG_TTL_MS = 10 * MINUTE;

// The moment an intake lead's clock starts. leadCadenceStartAt (migration
// 172) already carries the out-of-hours deferral and any manual Push; older
// rows fall back to the arrival, which is what they always used.
export const intakeStartOf = (lead) =>
  new Date(lead?.leadCadenceStartAt || lead?.leadCreatedOn);

/** Has this lead's clock not started yet (arrived after hours, or pushed)? */
export const intakeNotStarted = (lead, now = new Date()) =>
  !!lead?.leadCadenceStartAt && new Date(lead.leadCadenceStartAt) > now;

// The moment a waiting intake lead's turn comes and its 3-minute take window
// opens. The SERVER derives this (workCalendar.intakeRelease) and ships it as
// an absolute instant, so the client READS it rather than re-deriving the
// lunch and ladder math - no second implementation to drift. Falls back to
// the clock start, which is what it equals when nothing is owed.
export const intakeReleaseOf = (lead) =>
  new Date(
    lead?.intakeReleaseAt || lead?.leadCadenceStartAt || lead?.leadCreatedOn,
  );

// --- Lunch (Nic 2026-08-10) -------------------------------------------------
// The server resolves today's lunch windows to ABSOLUTE instants, so the
// client needs zero timezone math. A new lead's clock (intakeClockOf in
// LeadTile.js) and every step clock pause through the step math below
// (2026-10-21: one pause engine for lunch, meetings and breaks).

export const isOnLunch = (lunch, now = new Date()) =>
  !!lunch?.startAt &&
  !!lunch?.endAt &&
  now.getTime() >= new Date(lunch.startAt).getTime() &&
  now.getTime() < new Date(lunch.endAt).getTime();

// --- Derived lunch pause for STEP clocks (Nic 2026-08-11 pivot) ------------
// Windows stay EXACTLY as authored; the owner's lunch simply stops the
// clock. These mirror server/lib/workCalendar.js (lunchPauseCredit /
// effectiveStepWindow / lunchPausedNow) — keep the math identical. During
// the pause the credit grows 1:1 with `now`, so the effective deadline moves
// with the clock and the countdown NATURALLY freezes; after lunch (or an
// early check-in) the credit stops at exactly the pause consumed.

// BANKED credit (reassignment fairness, 2026-08-11): pause consumed under a
// PREVIOUS owner rides the step (meta.bankedLunchCreditMs, stamped at
// reassign) so the minutes survive the handoff; the companion reassign
// stamp clamps LIVE credit to pause consumed under the CURRENT owner only.
const bankedCreditOf = (step) => ({
  banked: Math.max(0, Number(step?.meta?.bankedLunchCreditMs || 0)),
  bankedAt: step?.meta?.bankedLunchCreditAt
    ? new Date(step.meta.bankedLunchCreditAt).getTime()
    : 0,
});

// ON A CALL (2026-11-02): a live call is a pause the server ships with an
// ESTIMATED end and `open: true`. Here it ends never: the payload is a
// snapshot (SSE + 30 s poll) and a tile must not unfreeze between refetches
// just because the estimate passed. Credit is capped at `now` anyway.
// Compare with it, never new Date() it.
export const pauseEndMs = (p) =>
  p?.open ? Infinity : new Date(p?.endAt).getTime();
export const pauseLiveAt = (p, t) =>
  !!p?.startAt &&
  !!p?.endAt &&
  new Date(p.startAt).getTime() <= t &&
  t < pauseEndMs(p);

// Which pause is freezing a clock at `now`, when several overlap: a call
// beats a meeting beats a break beats lunch. Reads the RAW intervals, never
// the merged blocks. Mirrors server/lib/workCalendar.js pauseKindAt.
const PAUSE_KIND_RANK = { call: 0, meeting: 1, away: 2, lunch: 3 };
export const pauseKindAt = (pause, now) => {
  const list = Array.isArray(pause) ? pause : pause ? [pause] : [];
  const t = new Date(now).getTime();
  let best = null;
  for (const p of list) {
    if (!pauseLiveAt(p, t)) continue;
    const kind = p.kind || 'lunch';
    if (
      best === null ||
      (PAUSE_KIND_RANK[kind] ?? 9) < (PAUSE_KIND_RANK[best] ?? 9)
    )
      best = kind;
  }
  return best;
};
// The live pause of the highest rank at `now`, as the object itself.
export const livePauseAt = (pause, now) => {
  const list = Array.isArray(pause) ? pause : pause ? [pause] : [];
  const t = new Date(now).getTime();
  let best = null;
  for (const p of list) {
    if (!pauseLiveAt(p, t)) continue;
    if (
      best === null ||
      (PAUSE_KIND_RANK[p.kind || 'lunch'] ?? 9) <
        (PAUSE_KIND_RANK[best.kind || 'lunch'] ?? 9)
    )
      best = p;
  }
  return best;
};

// MEETING PAUSE (Nic 2026-09-25): a pause is ONE interval (lunch) or a LIST
// (lunch + the owner's meetings, 5 minutes padded each side). Mirrors
// server/lib/workCalendar.js pauseIntervals / walkPauses exactly. The
// merged block's kind is a placeholder: the label comes from the raw
// interval under `now` (pauseKindAt).
const pauseIntervals = (pause) => {
  const list = Array.isArray(pause) ? pause : pause ? [pause] : [];
  const valid = list
    .filter((p) => p?.startAt && p?.endAt)
    .map((p) => ({
      startMs: new Date(p.startAt).getTime(),
      endMs: pauseEndMs(p),
      kind: p.kind || 'lunch',
    }))
    .filter((p) => p.endMs > p.startMs)
    .sort((a, b) => a.startMs - b.startMs);
  const merged = [];
  for (const p of valid) {
    const last = merged[merged.length - 1];
    if (last && p.startMs <= last.endMs) {
      if (p.endMs > last.endMs) last.endMs = p.endMs;
    } else {
      merged.push({ ...p });
    }
  }
  return merged;
};

const walkPauses = (step, pause, now) => {
  const intervals = pauseIntervals(pause);
  const out = { live: 0, startShift: 0, pausedKind: null };
  if (!intervals.length) return out;
  const { banked, bankedAt } = bankedCreditOf(step);
  let S = new Date(step.windowStartAt).getTime();
  let E = new Date(step.windowEndAt).getTime() + banked;
  const t = new Date(now).getTime();
  for (const { startMs: Ls, endMs: Le, kind } of intervals) {
    if (E <= Ls && S < Ls) break; // the clock was dead before this pause
    if (t >= Ls && t < Le && t >= Math.max(S, Ls)) out.pausedKind = kind;
    const credit = Math.max(0, Math.min(t, Le) - Math.max(S, Ls, bankedAt));
    if (credit <= 0) continue;
    if (S >= Ls && S < Le) {
      S += credit;
      out.startShift += credit;
    }
    E += credit;
    out.live += credit;
  }
  if (out.pausedKind) out.pausedKind = pauseKindAt(pause, now) || out.pausedKind;
  return out;
};

// The owner's pauses for step math: lunch + today's meetings, from a
// todaySchedule entry (lunchStartAt/lunchEndAt + meetingPauses).
export const ownerPausesOf = (entry) => {
  if (!entry) return null;
  const list = [
    ...(entry.lunchStartAt && entry.lunchEndAt
      ? [{ startAt: entry.lunchStartAt, endAt: entry.lunchEndAt, kind: 'lunch' }]
      : []),
    ...(entry.meetingPauses || []),
  ];
  return list.length ? list : null;
};

// No schedule entry for the owner today: the default lunch plus whatever
// meetings the server found for them (board.ownerMeetingPauses).
export const fallbackPausesOf = (defaultLunch, meetingPauses) => {
  const list = [
    ...(defaultLunch?.startAt && defaultLunch?.endAt
      ? [{ startAt: defaultLunch.startAt, endAt: defaultLunch.endAt, kind: 'lunch' }]
      : []),
    ...(meetingPauses || []),
  ];
  if (!list.length) return null;
  // Lunch alone keeps its old single-pause shape.
  return list.length === 1 && list[0].kind === 'lunch' ? defaultLunch : list;
};

// How many ms of the CURRENT owner's pause(s) has this step's clock absorbed
// as of `now` (live credit; banked rides separately)?
export const lunchPauseCredit = (step, pause, now = new Date()) =>
  walkPauses(step, pause, now).live;

// The step's EFFECTIVE window as of `now`: raw + banked + live credit. The
// end always shifts by the total; the start shifts only when the raw start
// sits inside a pause (a window opening mid-pause keeps its full length).
export const effectiveStepWindow = (step, pause, now = new Date()) => {
  const { banked } = bankedCreditOf(step);
  const { live, startShift } = walkPauses(step, pause, now);
  const credit = banked + live;
  return {
    credit,
    startAt: new Date(new Date(step.windowStartAt).getTime() + startShift),
    endAt: new Date(new Date(step.windowEndAt).getTime() + credit),
  };
};

// Which pause freezes this step RIGHT NOW: 'lunch' | 'meeting' | 'away' |
// 'call' | null. (Automated steps never pause; the sweep sends them
// regardless.)
export const stepPausedKindNow = (step, pause, now = new Date()) => {
  if (!step || step.status !== 'pending') return null;
  if (step.meta?.autoSend && !step.meta?.autoSendBlocked) return null;
  return walkPauses(step, pause, now).pausedKind;
};

export const stepLunchPausedNow = (step, pause, now = new Date()) =>
  !!stepPausedKindNow(step, pause, now);

// Cadence tiles: tone follows the current step's window.
export const stepTone = (step, now = new Date()) => {
  if (!step) return { stripe: STRIPE.quiet, state: 'idle' };
  // An automated step sends itself when its window opens — no human urgency,
  // so purple replaces the whole upcoming/due escalation. A BLOCKED one
  // (unfilled bio) is manual work again and falls through to normal tones.
  if (
    step.meta?.autoSend &&
    !step.meta?.autoSendBlocked &&
    step.status === 'pending'
  ) {
    return { stripe: STRIPE.automated, state: 'automated' };
  }
  if (step.status === 'missed')
    return { stripe: STRIPE.overdue, state: 'overdue' };
  const ts = now.getTime();
  const start = new Date(step.windowStartAt).getTime();
  const end = new Date(step.windowEndAt).getTime();
  if (ts < start) return { stripe: STRIPE.upcoming, state: 'upcoming' };
  if (ts <= end) {
    // In the window = amber, the WHOLE window (the legend's promise: red
    // only when truly behind). The burn-down bar carries "running out".
    const pct = (ts - start) / Math.max(end - start, 1);
    return { stripe: STRIPE.due, state: 'due', pct };
  }
  return { stripe: STRIPE.overdue, state: 'overdue' };
};

// Contact Made dwell clock — the anti-aging mechanism: this column must not
// become a parking lot, so the stripe escalates with time since contact.
export const dwellTone = (contactMadeAt, now = new Date()) => {
  const dwell = now.getTime() - new Date(contactMadeAt).getTime();
  if (dwell <= 2 * HOUR) return { stripe: STRIPE.contactFresh, level: 'fresh' };
  if (dwell <= 6 * HOUR) return { stripe: STRIPE.due, level: 'aging' };
  return { stripe: STRIPE.overdue, level: 'stale' };
};

export const dwellLabel = (contactMadeAt, now = new Date()) =>
  shortDuration(now.getTime() - new Date(contactMadeAt).getTime());
